import { useEffect, useState } from "react";
import { API } from "service/Interceptor";

const cache = new Map(); // `${stateId}:${cityId}` -> { city, state }

/**
 * Resolve a readable "City, State" string from profile fields.
 * Falls back to looking up names via /api/locations when only IDs are present.
 */
export function useResolvedLocation(profile) {
  const cityName =
    profile?.cityName ||
    profile?.city?.name ||
    profile?.companyResponseDto?.cityName ||
    profile?.companyResponseDto?.city?.name ||
    profile?.companyResponseDto?.locationCity ||
    "";
  const stateName =
    profile?.companyResponseDto?.locationCountry ||
    profile?.stateName ||
    profile?.state?.name ||
    profile?.companyResponseDto?.stateName ||
    profile?.companyResponseDto?.state?.name ||
    "";
  const cityId =
    profile?.cityId ||
    profile?.city?.id ||
    profile?.companyResponseDto?.cityId ||
    null;
  const stateId =
    profile?.stateId ||
    profile?.state?.id ||
    profile?.companyResponseDto?.stateId ||
    null;

  const direct = [cityName, stateName].filter(Boolean).join(", ");
  const [resolved, setResolved] = useState(direct);

  useEffect(() => {
    setResolved(direct);
    if (direct || (!cityId && !stateId)) return;

    const key = `${stateId || ""}:${cityId || ""}`;
    if (cache.has(key)) {
      const hit = cache.get(key);
      setResolved([hit.city, hit.state].filter(Boolean).join(", "));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        let stateLabel = stateName;
        let cityLabel = cityName;

        if (stateId && !stateLabel) {
          const res = await API.get("api/locations/states");
          const list =
            res?.data?.data?.results || res?.data?.data || res?.data || [];
          const match = (Array.isArray(list) ? list : []).find(
            (s) => String(s.id) === String(stateId)
          );
          stateLabel = match?.name || "";
        }

        if (cityId && stateId && !cityLabel) {
          const res = await API.get(`api/locations/states/${stateId}/cities`);
          const list =
            res?.data?.data?.results || res?.data?.data || res?.data || [];
          const match = (Array.isArray(list) ? list : []).find(
            (c) => String(c.id) === String(cityId)
          );
          cityLabel = match?.name || "";
        }

        const label = [cityLabel, stateLabel].filter(Boolean).join(", ");
        cache.set(key, { city: cityLabel, state: stateLabel });
        if (!cancelled && label) setResolved(label);
      } catch (_) {
        /* keep direct */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [direct, cityId, stateId, cityName, stateName]);

  return resolved;
}

export default useResolvedLocation;
